/**
 * InlineVideoPlayer - Compact video player for frames and segments
 * Features: Tap to play/pause, auto-loop for segments, minimal controls
 */

import { clsx } from 'clsx'
import { useCallback, useRef, useState } from 'react'

import styles from 'components/agents/InlineVideoPlayer.module.css'
import {
  applyAspectRatio,
  canSeekToTime,
  getMediaEventCurrentTime,
  getMuxLoopStartTime,
} from 'components/agents/inlineVideoPlayerMedia'
import { MuxPlaybackPlayer } from 'components/shared/MuxPlaybackPlayer'
import { Button } from 'components/ui/Button'
import { webLogger } from 'utils/logger'
import { SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from 'utils/units'

import type MuxPlayerElement from '@mux/mux-player'
import type { ReactElement } from 'react'
import type { MuxPlaybackEmbed } from 'utils/videoUrlResolver'

const MINUTE_TIMESTAMP_PART_COUNT = 2
const HOUR_TIMESTAMP_PART_COUNT = 3
const POSTER_JPEG_QUALITY = 0.82

const timeToSeconds = (time: string): number => {
  const parts = time.split(':').map(Number)
  if (parts.length === MINUTE_TIMESTAMP_PART_COUNT) {
    return parts[0] * SECONDS_PER_MINUTE + parts[1]
  }
  if (parts.length === HOUR_TIMESTAMP_PART_COUNT) {
    return parts[0] * SECONDS_PER_HOUR + parts[1] * SECONDS_PER_MINUTE + parts[2]
  }
  return 0
}

const capturePosterFrame = (video: HTMLVideoElement): string | undefined => {
  if (video.videoWidth <= 0 || video.videoHeight <= 0) {
    return undefined
  }

  const canvas = document.createElement('canvas')
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight

  const context = canvas.getContext('2d')
  if (!context) {
    return undefined
  }

  try {
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', POSTER_JPEG_QUALITY)
  } catch (error) {
    webLogger.warn('Inline video poster frame capture failed', {
      error: error instanceof Error ? error.message : String(error),
    })
    return undefined
  } finally {
    canvas.remove()
  }
}

export interface InlineVideoPlayerProperties {
  videoUrl: string
  mux?: MuxPlaybackEmbed
  startTime?: string
  endTime?: string
  isLoop?: boolean
  className?: string
  poster?: string
  playbackRate?: number
  onControllerReady?: (controller: InlineVideoPlayerController) => void
  onTimeUpdate?: (currentTime: number) => void
  onPlayStateChange?: (isPlaying: boolean) => void
}

export interface InlineVideoPlayerController {
  pause: () => void
  play: () => void
  seekTo: (timeSeconds: number) => void
  setPlaybackRate: (playbackRate: number) => void
}

export const InlineVideoPlayer = ({
  videoUrl,
  mux,
  startTime,
  endTime,
  isLoop = false,
  className = '',
  poster,
  playbackRate = 1,
  onControllerReady,
  onTimeUpdate,
  onPlayStateChange,
}: InlineVideoPlayerProperties): ReactElement => {
  /* --- Section 1: Hooks --- */
  const videoReference = useRef<HTMLVideoElement | null>(null)
  const muxPlayerReference = useRef<MuxPlayerElement | null>(null)
  const containerReference = useRef<HTMLDivElement | null>(null)

  /* --- Section 2: Local State --- */
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [extractedPosterSource, setExtractedPosterSource] = useState<string | undefined>(undefined)

  /* --- Section 3: Derived Values --- */
  const startSeconds = startTime ? timeToSeconds(startTime) : undefined
  const endSeconds = endTime ? timeToSeconds(endTime) : undefined
  const posterSource = poster ?? mux?.thumbnailUrl ?? extractedPosterSource
  const shouldShowPosterBackdrop = posterSource !== undefined && !hasInteracted
  const playerClassName = clsx(styles.player, shouldShowPosterBackdrop && styles.playerWithPosterBackdrop, className)

  /* --- Section 4: Event Handlers --- */
  const handleControllerReady = useCallback(() => {
    onControllerReady?.({
      pause: () => {
        const media = muxPlayerReference.current ?? videoReference.current
        media?.pause()
      },
      play: () => {
        const media = muxPlayerReference.current ?? videoReference.current
        if (!media) {
          return
        }
        if (startSeconds !== undefined && media.currentTime === 0) {
          media.currentTime = getMuxLoopStartTime(media, startSeconds)
        }
        media.play().catch(() => undefined)
      },
      seekTo: (timeSeconds: number) => {
        const media = muxPlayerReference.current ?? videoReference.current
        if (media && canSeekToTime(media, timeSeconds)) {
          media.currentTime = timeSeconds
        }
      },
      setPlaybackRate: (nextPlaybackRate: number) => {
        const media = muxPlayerReference.current ?? videoReference.current
        if (media) {
          media.playbackRate = nextPlaybackRate
        }
      },
    })
  }, [onControllerReady, startSeconds])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoReference.current
    if (!video) {
      return
    }

    applyAspectRatio(containerReference.current, video)
    handleControllerReady()

    if (startSeconds === undefined) {
      video.playbackRate = playbackRate
      return
    }

    video.currentTime = startSeconds
    video.playbackRate = playbackRate
  }, [handleControllerReady, playbackRate, startSeconds])

  const handleHtmlVideoSeeked = useCallback(() => {
    const video = videoReference.current
    if (posterSource !== undefined || hasInteracted || !video) {
      return
    }

    setExtractedPosterSource(capturePosterFrame(video))
  }, [hasInteracted, posterSource])

  const handleHtmlVideoTimeUpdate = useCallback(() => {
    const video = videoReference.current
    if (!video) {
      return
    }

    onTimeUpdate?.(video.currentTime)

    if (endSeconds === undefined || startSeconds === undefined) {
      return
    }

    if (video.currentTime < endSeconds) {
      return
    }

    if (isLoop) {
      video.currentTime = startSeconds
      if (hasInteracted) {
        video.play().catch(() => undefined)
      }
      return
    }

    video.pause()
    setIsPlaying(false)
    onPlayStateChange?.(false)
  }, [endSeconds, hasInteracted, isLoop, onPlayStateChange, onTimeUpdate, startSeconds])

  const handleHtmlVideoPlay = useCallback(() => {
    setIsPlaying(true)
    onPlayStateChange?.(true)
  }, [onPlayStateChange])

  const handleHtmlVideoPause = useCallback(() => {
    setIsPlaying(false)
    onPlayStateChange?.(false)
  }, [onPlayStateChange])

  const handleVideoClick = useCallback(() => {
    const video = videoReference.current
    if (!video) {
      return
    }

    setHasInteracted(true)

    if (isPlaying) {
      video.pause()
      return
    }

    if (startSeconds !== undefined && video.currentTime === 0) {
      video.currentTime = startSeconds
    }

    video.play().catch((error_: unknown) => {
      webLogger.error('Inline video play failed', {
        error: error_ instanceof Error ? error_.message : String(error_),
      })
    })
  }, [isPlaying, startSeconds])

  const handleMuxLoadedMetadata = useCallback(() => {
    const player = muxPlayerReference.current
    if (!player) {
      return
    }

    applyAspectRatio(containerReference.current, player)
    handleControllerReady()

    if (startSeconds === undefined || !canSeekToTime(player, startSeconds)) {
      player.playbackRate = playbackRate
      return
    }

    player.currentTime = startSeconds
    player.playbackRate = playbackRate
  }, [handleControllerReady, playbackRate, startSeconds])

  const handleMuxTimeUpdate = useCallback(
    (event: Event) => {
      const player = muxPlayerReference.current
      const currentTime = getMediaEventCurrentTime(event) ?? player?.currentTime

      if (currentTime !== undefined) {
        onTimeUpdate?.(currentTime)
      }

      if (!player || endSeconds === undefined || currentTime === undefined || currentTime < endSeconds) {
        return
      }

      if (isLoop) {
        player.currentTime = getMuxLoopStartTime(player, startSeconds)
        player.play().catch(() => undefined)
        return
      }

      player.pause()
      setIsPlaying(false)
      onPlayStateChange?.(false)
    },
    [endSeconds, isLoop, onPlayStateChange, onTimeUpdate, startSeconds]
  )

  const handleMuxPlay = useCallback(() => {
    setHasInteracted(true)
    setIsPlaying(true)
    onPlayStateChange?.(true)

    // Signed playback tokens strip `asset_start_time` from the stream URL, so the
    // player can begin at 0. Mirror iOS: snap playback back into the clip window.
    const player = muxPlayerReference.current
    if (!player || startSeconds === undefined || player.currentTime >= startSeconds) {
      return
    }

    player.currentTime = getMuxLoopStartTime(player, startSeconds)
  }, [onPlayStateChange, startSeconds])

  const handleMuxPause = useCallback(() => {
    setIsPlaying(false)
    onPlayStateChange?.(false)
  }, [onPlayStateChange])

  /* --- Section 5: Early Returns --- */
  if (mux) {
    return (
      <div ref={containerReference} className={playerClassName}>
        {shouldShowPosterBackdrop ? (
          <img className={styles.posterBackdrop} src={posterSource} alt="" aria-hidden="true" />
        ) : undefined}
        <MuxPlaybackPlayer
          ref={muxPlayerReference}
          className={styles.element}
          playbackId={mux.playbackId}
          tokens={mux.tokens}
          streamType="on-demand"
          startTime={startSeconds}
          assetStartTime={startSeconds}
          assetEndTime={endSeconds}
          poster={poster ?? mux.thumbnailUrl}
          isLoop={isLoop}
          onLoadedMetadata={handleMuxLoadedMetadata}
          onTimeUpdate={handleMuxTimeUpdate}
          onPlay={handleMuxPlay}
          onPause={handleMuxPause}
        />
      </div>
    )
  }

  /* --- Section 6: Render --- */
  return (
    <div ref={containerReference} className={playerClassName}>
      {shouldShowPosterBackdrop ? (
        <img className={styles.posterBackdrop} src={posterSource} alt="" aria-hidden="true" />
      ) : undefined}
      <video
        key={videoUrl}
        ref={videoReference}
        src={videoUrl}
        className={styles.element}
        playsInline
        preload="metadata"
        poster={poster}
        crossOrigin="anonymous"
        onLoadedMetadata={handleLoadedMetadata}
        onSeeked={handleHtmlVideoSeeked}
        onTimeUpdate={handleHtmlVideoTimeUpdate}
        onPlay={handleHtmlVideoPlay}
        onPause={handleHtmlVideoPause}
      >
        <track kind="captions" srcLang="en" label="English captions" src="data:text/vtt,WEBVTT" />
      </video>
      <Button
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        className={clsx(styles.hitarea, isPlaying && styles.hitareaPlaying)}
        onClick={handleVideoClick}
        variant="plain"
      >
        {isPlaying ? (
          <span className="visually-hidden">Pause video</span>
        ) : (
          <span className={styles.overlay}>
            <span className={styles.playIcon}>▶</span>
          </span>
        )}
      </Button>
    </div>
  )
}
